import { FederatedPointerEvent, Point, Texture } from "pixi.js";
import { AnimatedButton } from "./ui/AnimatedButton";
import { gsap } from "gsap/gsap-core";

export class ButtonWithScaleAnimation extends AnimatedButton {
    private static readonly CLICK_ANIMATION_DURATION = 0.4;
    private static readonly ANIMATION_SCALE = 0.9;
    private readonly animationStartScale: Point;
    private readonly animationStartPosition: Point;
    private readonly animationStartSize: Point;

    public constructor(texture: Texture, alpha: number = 1) {
        super({
            defaultTexture: texture
        });

        this.options.pointerClickAnimation = new gsap.core.Timeline({ paused: true });
        this.options.pointerDownAnimation = new gsap.core.Timeline({ paused: true });
        this.animationStartScale = this.scale.clone();
        this.animationStartPosition = this.position.clone();
        this.animationStartSize = new Point(this.width, this.height);

        this.updateAnimation();
        this.alpha = alpha;
    }

    protected override handlePointerUp(e: FederatedPointerEvent): void {
        super.handlePointerUp(e);
        this.checkAndUpdateButtonParams();
    }

    protected override handlePointerDown(e: FederatedPointerEvent): void {
        this.checkAndUpdateButtonParams();
        super.handlePointerDown(e);
    }

    protected override handlePointerOver(e: FederatedPointerEvent): void {
        this.checkAndUpdateButtonParams();
        super.handlePointerOver(e);
    }

    protected override handlePointerUpOutside(e: FederatedPointerEvent): void {
        super.handlePointerUpOutside(e);
        this.checkAndUpdateButtonParams();
    }

    protected override handlePointerOut(e: FederatedPointerEvent): void {
        super.handlePointerOut(e);
        this.checkAndUpdateButtonParams();
    }

    private checkAndUpdateButtonParams(): void {
        if (!this.animationStartScale.equals(this.scale) || !this.animationStartPosition.equals(this.position)
            || this.animationStartSize.x !== this.width || this.animationStartSize.y !== this.height) {
            this.animationStartScale.copyFrom(this.scale);
            this.animationStartPosition.copyFrom(this.position);
            this.animationStartSize.set(this.width, this.height);
            this.updateAnimation();
        }
    }

    private updateAnimation(): void {
        if (this.isAnimationPlayed && !this.visible && !this.enabled) {
            return;
        }
        this.options.pointerDownAnimation.clear();
        this.options.pointerDownAnimation.fromTo(this.scale,
            {
                x: this.animationStartScale.x,
                y: this.animationStartScale.y
            },
            {
                x: this.animationStartScale.x * ButtonWithScaleAnimation.ANIMATION_SCALE,
                y: this.animationStartScale.y * ButtonWithScaleAnimation.ANIMATION_SCALE,
                duration: ButtonWithScaleAnimation.CLICK_ANIMATION_DURATION * 0.5
            }, "<"
        );
        const width = this.width - this.pivot.x;
        const height = this.height - this.pivot.y;
        this.options.pointerDownAnimation.fromTo(this.position,
            {
                x: this.animationStartPosition.x,
                y: this.animationStartPosition.y
            },
            {
                x: this.animationStartPosition.x + (width - width * ButtonWithScaleAnimation.ANIMATION_SCALE) * 0.5,
                y: this.animationStartPosition.y + (height - height * ButtonWithScaleAnimation.ANIMATION_SCALE) * 0.5,
                duration: ButtonWithScaleAnimation.CLICK_ANIMATION_DURATION * 0.5
            }, "<"
        );

        this.options.pointerClickAnimation.clear();
        this.options.pointerClickAnimation.to(this.scale,
            {
                x: this.animationStartScale.x,
                y: this.animationStartScale.y,
                duration: ButtonWithScaleAnimation.CLICK_ANIMATION_DURATION * 0.5
            }, "<"
        );
        this.options.pointerClickAnimation.to(this.position,
            {
                x: this.animationStartPosition.x,
                y: this.animationStartPosition.y,
                duration: ButtonWithScaleAnimation.CLICK_ANIMATION_DURATION * 0.5
            }, "<"
        );
    }
}
